import React, { useEffect, useState } from 'react'

import { API } from '@enkla-gui'
import DatePicker from 'react-datepicker'
import { icons } from '@config'
import sv from 'date-fns/locale/sv'

const FILTER_OPERATORS = [
  { key: 'Innehåller', value: 'like' },
  { key: 'Innehåller inte', value: 'not_like' },
  { key: 'Är lika med', value: '=' },
  { key: 'Är inte lika med', value: '!=' },
  { key: 'Större än', value: '>' },
  { key: 'Mindre än', value: '<' },
  { key: 'Större än eller lika med', value: '>=' },
  { key: 'Mindre än eller lika med', value: '<=' },
  { key: 'Är tom', value: 'null' },
  { key: 'Är inte tom', value: 'not_null' },
  { key: 'Är sant', value: 'scope' }
]

const DATE_OPERATORS = [
  { key: 'Den här månaden', value: 'this_month' },
  { key: 'Förra månaden', value: 'last_month' },
  { key: 'I år', value: 'this_year' },
  { key: 'Förra året', value: 'last_year' },
  { key: 'Denna vecka', value: 'this_week' },
  { key: 'Förra veckan', value: 'last_week' },
  { key: 'Idag', value: 'today' },
  { key: 'Igår', value: 'yesterday' }
]

const NO_VALUE_OPERATORS = ['null', 'not_null', 'this_month', 'last_month', 'this_year', 'last_year', 'this_week', 'last_week', 'today', 'yesterday', 'scope']

const getOperatorsForFieldType = (fieldType) => {
  switch (fieldType) {
    case 'number':
      return FILTER_OPERATORS.filter(op =>
        ['=', '!=', '>', '<', '>=', '<=', 'null', 'not_null'].includes(op.value)
      )
    case 'boolean':
      return FILTER_OPERATORS.filter(op =>
        ['=', '!=', 'null', 'not_null'].includes(op.value)
      )
    case 'datetime':
      return [
        ...DATE_OPERATORS,
        ...FILTER_OPERATORS.filter(op =>
          ['=', '!=', '>', '<', '>=', '<=', 'null', 'not_null'].includes(op.value)
        )
      ]
    case 'scope':
      return FILTER_OPERATORS.filter(op => op.value === 'scope')
    default:
      return FILTER_OPERATORS
  }
}

const getInputType = (fieldType) => {
  switch (fieldType) {
    case 'number':
      return 'number'
    case 'email':
      return 'email'
    case 'datetime':
      return 'datetime-local'
    default:
      return 'text'
  }
}

const getFieldType = (fieldKey, fields) => {
  const field = fields.find(f => f.key === fieldKey)
  return field?.type || 'string'
}

export default function ReportFilters({ model, fields, filters, onChange }) {
  const [newFilter, setNewFilter] = useState({
    field: '',
    operator: '=',
    value: ''
  })
  const [enumOptions, setEnumOptions] = useState([])
  const [loadingEnumOptions, setLoadingEnumOptions] = useState(false)
  const [enumCache, setEnumCache] = useState({})

  const selectedField = fields.find(f => f.key === newFilter.field)
  const availableOperators = selectedField ? getOperatorsForFieldType(selectedField.type) : FILTER_OPERATORS

  // Fetch enum options when field changes and is enum type
  useEffect(() => {
    if (selectedField && selectedField.type === 'enum') {
      const cacheKey = `${model}.${selectedField.key}`

      if (enumCache[cacheKey]) {
        setEnumOptions(enumCache[cacheKey])
        return
      }

      setLoadingEnumOptions(true)
      API.get(`api/report/models/${model}/fields/${selectedField.key}/enum-options`)
        .then(options => {
          const optionsArray = options || []
          setEnumOptions(optionsArray)
          setEnumCache(prev => ({ ...prev, [cacheKey]: optionsArray }))
          setLoadingEnumOptions(false)
        })
        .catch(error => {
          console.error('Error fetching enum options:', error)
          setEnumOptions([])
          setLoadingEnumOptions(false)
        })
    } else {
      setEnumOptions([])
    }
  }, [selectedField, model, enumCache])

  const addFilter = () => {
    if (newFilter.field && (NO_VALUE_OPERATORS.includes(newFilter.operator) || newFilter.value)) {
      const updatedFilters = [...filters, { ...newFilter }]
      onChange(updatedFilters)
      setNewFilter({ field: '', operator: '=', value: '' })
    }
  }

  const removeFilter = (index) => {
    const updatedFilters = filters.filter((_, i) => i !== index)
    onChange(updatedFilters)
  }

  // Helper function to get enum label for a field/value combination
  const getEnumLabel = async (fieldKey, value) => {
    const field = fields.find(f => f.key === fieldKey)
    if (!field || field.type !== 'enum') {
      return value
    }

    const cacheKey = `${model}.${fieldKey}`

    if (enumCache[cacheKey]) {
      const option = enumCache[cacheKey].find(opt => opt.value === value)
      return option ? option.label : value
    }

    try {
      const options = await API.get(`api/report/models/${model}/fields/${fieldKey}/enum-options`)
      setEnumCache(prev => ({ ...prev, [cacheKey]: options || [] }))
      const option = (options || []).find(opt => opt.value === value)
      return option ? option.label : value
    } catch (error) {
      console.error('Error fetching enum options for translation:', error)
      return value
    }
  }

  // Hook to translate enum values in filters
  const [translatedFilters, setTranslatedFilters] = useState([])

  useEffect(() => {
    const translateFilters = async () => {
      const translated = await Promise.all(
        filters.map(async (filter) => {
          const field = fields.find(f => f.key === filter.field)
          if (field && field.type === 'enum' && !['null', 'not_null'].includes(filter.operator)) {
            const label = await getEnumLabel(filter.field, filter.value)
            return { ...filter, displayValue: label }
          }
          return { ...filter, displayValue: filter.value }
        })
      )
      setTranslatedFilters(translated)
    }

    if (filters.length > 0) {
      translateFilters()
    } else {
      setTranslatedFilters([])
    }
  }, [filters, model, fields, enumCache])

  return (
    <div>
      {filters.length > 0 && (
        <div class='mb-4'>
          <div class='text-muted small text-uppercase mb-2'>
            <i class={icons.filter + ' me-1'}></i> Aktiva filter
          </div>
          <div class='d-flex flex-wrap gap-2'>
            {translatedFilters.map((filter, index) => {
              const field = fields.find(f => f.key === filter.field)

              // Look for operator in both FILTER_OPERATORS and DATE_OPERATORS
              let operator = FILTER_OPERATORS.find(op => op.value === filter.operator)
              if (!operator) {
                operator = DATE_OPERATORS.find(op => op.value === filter.operator)
              }

              return (
                <span key={index} class='badge bg-dark d-inline-flex align-items-center gap-2 py-2 px-3'>
                  <span>{filter.label || field?.label || filter.field}</span>
                  <span class='opacity-75'>{operator?.key || filter.operator}</span>
                  {!NO_VALUE_OPERATORS.includes(filter.operator) && (
                    <span>{filter.displayValue || filter.value}</span>
                  )}
                  <button
                    type='button'
                    class='btn-close btn-close-white ms-1'
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => removeFilter(index)}
                    aria-label='Ta bort filter'
                  ></button>
                </span>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <div class='text-muted small text-uppercase mb-3'>
          <i class={icons.plus + ' me-1'}></i> Lägg till filter
        </div>

        <div class='row g-3'>
          <div class='col-12 col-md-4'>
            <label class='form-label small text-uppercase'>Fält</label>
            <select
              class='form-select'
              value={newFilter.field}
              onChange={(e) => {
                const selectedField = fields.find(f => f.key === e.target.value)
                if (selectedField?.type === 'scope') {
                  setNewFilter({ field: e.target.value, operator: 'scope', value: true })
                } else {
                  setNewFilter({ ...newFilter, field: e.target.value, operator: '=' })
                }
              }}
            >
              <option value=''>Välj...</option>
              {fields.map(field => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>

          <div class='col-12 col-md-3'>
            <label class='form-label small text-uppercase'>Operator</label>
            <select
              class='form-select'
              value={newFilter.operator}
              onChange={(e) => setNewFilter({ ...newFilter, operator: e.target.value })}
              disabled={!newFilter.field}
            >
              <option value=''>Välj...</option>
              {availableOperators.map(operator => (
                <option key={operator.value} value={operator.value}>
                  {operator.key}
                </option>
              ))}
            </select>
          </div>

          <div class='col-12 col-md-3'>
            {!NO_VALUE_OPERATORS.includes(newFilter.operator) && (
              <>
                <label class='form-label small text-uppercase'>Värde</label>
                {selectedField?.type === 'enum' ? (
                  <select
                    class='form-select'
                    value={newFilter.value}
                    onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                    disabled={!newFilter.field || loadingEnumOptions}
                  >
                    <option value=''>
                      {loadingEnumOptions ? 'Laddar...' : 'Välj...'}
                    </option>
                    {enumOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : selectedField?.type === 'datetime' ? (
                  <DatePicker
                    className='form-control'
                    locale={sv}
                    dateFormat='yyyy-MM-dd'
                    placeholderText='Välj datum...'
                    selected={newFilter.value ? new Date(newFilter.value) : null}
                    onChange={(date) => {
                      const dateValue = date ? date.toISOString().split('T')[0] : ''
                      setNewFilter({ ...newFilter, value: dateValue })
                    }}
                    disabled={!newFilter.field}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                ) : (
                  <input
                    type={getInputType(selectedField?.type)}
                    class='form-control'
                    placeholder='Ange värde...'
                    value={newFilter.value}
                    onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                    disabled={!newFilter.field}
                  />
                )}
              </>
            )}
          </div>

          <div class='col-12 col-md-2 d-flex align-items-end'>
            <button
              type='button'
              class='btn btn-primary w-100'
              onClick={addFilter}
              disabled={
                !newFilter.field ||
                (!NO_VALUE_OPERATORS.includes(newFilter.operator) && !newFilter.value)
              }
            >
              <i class={icons.plus}></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
